if (!customElements.get('momento-product-bundles')) {
  class MomentoProductBundles extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.options = [...this.querySelectorAll('[data-bundle-option]')];
      this.radios = [...this.querySelectorAll('input[name="momento-bundle"]')];
      this.selects = [...this.querySelectorAll('[data-bundle-color-select]')];
      this.addButton = this.querySelector('[data-bundle-add]');
      this.status = this.querySelector('[data-bundle-status]');
      this.activeQuantity = 1;
      this.submitting = false;

      try {
        this.products = JSON.parse(this.querySelector('[data-bundle-products]').textContent);
      } catch (_error) {
        return;
      }

      this.normalizeUnavailableSelections();
      this.bindEvents();
      this.updateBundle();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    bindEvents() {
      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.activeQuantity = Number(radio.value);
          this.updateBundle();
        }, { signal: this.signal });
      });

      this.selects.forEach((select) => {
        select.addEventListener('change', () => this.updateColorImage(select), { signal: this.signal });
      });

      this.addButton?.addEventListener('click', () => this.addBundleToCart(), { signal: this.signal });
      document.addEventListener('momento:product-color-change', () => {
        if (this.activeQuantity === 1) this.updateBundle();
      }, { signal: this.signal });
    }

    updateBundle() {
      this.options.forEach((option) => {
        const selected = Number(option.dataset.bundleOption) === this.activeQuantity;
        option.classList.toggle('active', selected);
        const colors = option.querySelector('.momento-bundle-colors');
        if (colors) colors.hidden = !selected;
      });

      const activeOption = this.activeOption;
      if (this.addButton && activeOption) {
        const available = this.selectionAvailable;
        this.addButton.textContent = available ? `ADD TO CART — ${activeOption.dataset.displayTotal}` : 'SOLD OUT';
        this.addButton.disabled = !available;
        this.addButton.classList.toggle('is-sold-out', !available);
      }
      if (this.status) this.status.textContent = '';
    }

    updateColorImage(select) {
      const selectedOption = select.selectedOptions[0];
      const image = select.closest('.momento-bundle-color-row')?.querySelector('[data-bundle-color-image]');
      if (image && selectedOption?.dataset.image) image.src = selectedOption.dataset.image;
      if (this.addButton) this.addButton.disabled = !this.selectionAvailable;
    }

    normalizeUnavailableSelections() {
      this.selects.forEach((select) => {
        const selected = select.selectedOptions[0];
        if (selected && !selected.disabled && this.products[select.value]?.available) return;

        const fallback = [...select.options].find((option) => (
          !option.disabled && this.products[option.value]?.available
        ));
        if (fallback) select.value = fallback.value;
        this.updateColorImage(select);
      });
    }

    get activeOption() {
      return this.options.find((option) => Number(option.dataset.bundleOption) === this.activeQuantity);
    }

    get selectedColors() {
      if (this.activeQuantity === 1) {
        const mainContent = this.closest('#MainContent');
        const pdpColor = mainContent?.querySelector('momento-product-pdp')?.dataset.currentColor;
        const homepageColor = mainContent?.querySelector('momento-product-showcase [data-product-color][aria-checked="true"]')?.dataset.productColor;
        return [pdpColor || homepageColor || 'silver-black'];
      }
      return [...this.activeOption.querySelectorAll('[data-bundle-color-select]')].map((select) => select.value);
    }

    get selectionAvailable() {
      return this.selectedColors.every((color) => this.products[color]?.available);
    }

    async addBundleToCart() {
      if (this.submitting || !this.addButton || !this.selectionAvailable) return;

      const quantities = this.selectedColors.reduce((items, color) => {
        const variantId = this.products[color].variantId;
        items[variantId] = (items[variantId] || 0) + 1;
        return items;
      }, {});
      const items = Object.entries(quantities).map(([id, quantity]) => ({ id: Number(id), quantity }));
      const label = this.addButton.textContent;

      this.submitting = true;
      this.addButton.disabled = true;
      this.addButton.textContent = 'ADDING…';
      if (this.status) this.status.textContent = '';

      try {
        if (window.MomentoCart?.addItems) {
          const added = await window.MomentoCart.addItems(items);
          if (!added) throw new Error('Unable to add this bundle to the cart.');
        } else {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items })
          });
          if (!response.ok) throw new Error('Unable to add this bundle to the cart.');
          window.location.assign(this.dataset.cartUrl || '/cart');
          return;
        }
        this.submitting = false;
        this.addButton.disabled = false;
        this.addButton.textContent = label;
      } catch (error) {
        this.submitting = false;
        this.addButton.disabled = false;
        this.addButton.textContent = label;
        if (this.status) this.status.textContent = error.message;
      }
    }
  }

  customElements.define('momento-product-bundles', MomentoProductBundles);
}
